import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { analyticsStore, fetchScreens } from "../stores/analytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, ArrowRight } from "lucide-react";
import { formatNumber, formatDurationHuman } from "@/lib/utils";

export default function AnalyticsScreens() {
  const { screens, timeRange } = useSnapshot(analyticsStore);
  const { data, loading, error } = screens;

  useEffect(() => {
    fetchScreens();
  }, [timeRange]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Screen Views Table */}
        <Card>
          <CardHeader>
            <CardTitle>Screen Views</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Screen</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Avg Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.screens.map((screen) => (
                    <TableRow key={screen.screen_name}>
                      <TableCell className="font-medium">
                        {screen.screen_name}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(screen.view_count)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(screen.unique_users)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatDurationHuman(screen.avg_time_on_screen_seconds)}
                      </TableCell>
                    </TableRow>
                  )) ?? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-muted-foreground"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Navigation Paths */}
        <Card>
          <CardHeader>
            <CardTitle>Navigation Paths</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {data?.navigation_paths.slice(0, 10).map((path, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{path.from_screen}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{path.to_screen}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatNumber(path.count)} times
                    </span>
                  </div>
                )) ?? (
                  <p className="text-center text-muted-foreground">
                    No data available
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
